import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import event from '../../../utils/analytics/event';
import { ModalCloseButton, ModalContainer, ModalHeader, ModalOverlay, ModalWrapper } from './Modal.styles';
import createThemedComponent from '../../../theming/utils/createThemedComponent';
/**
 * Themeable Modal that acts as a canvas for all Modal displays. Designed to be
 * used with the `useModal` hook. See [useModal.js](https://git.cogolo.net/minerva/pantheon/blob/master/src/components/hooks/useModal/useModal.js)
 *
 * ## Piwik Event tracking
 *
 * By default, it logs 2 possible piwik events:
 *
 * - `event(['click', <modal name>, 'close-overlay']);`
 * - `event(['click', <modal name>, 'close-x-button']);`
 *
 * If using the default piwik events, then a value for the `modalName` prop must be passed in.
 *
 * If an object is passed for the piwikEvents param, then 2 custom piwik events will be logged instead of the default options.
 *
 * `piwikEvents` must be passed in as such: `{overlay: [], closeButton:[]}`
 *
 * ```js
 * import { Modal } from '@minerva-analytics/pantheon';
 *
 * const { isShowing, toggleModal } = useModal();
 * <Modal isShowing={isShowing} hide={toggleModal} backdropClosesModal>
 *   <p>I'm a modal!</p>
 * </Modal>
 * ```
 * **Default Styles**
 *
 * To view the component's styling directly, see [Modal.styles.js](https://git.cogolo.net/minerva/pantheon/blob/master/src/components/elements/Modal/Modal.styles.js)
 *
 */

export var ModalComponent = function ModalComponent(_ref) {
  var isShowing = _ref.isShowing,
      hide = _ref.hide,
      children = _ref.children,
      backdropClosesModal = _ref.backdropClosesModal,
      modalName = _ref.modalName,
      piwikEvents = _ref.piwikEvents,
      rest = _objectWithoutPropertiesLoose(_ref, ["isShowing", "hide", "children", "backdropClosesModal", "modalName", "piwikEvents"]);

  var handleBackDropClick = function handleBackDropClick(e) {
    if (e.target !== e.currentTarget) return;
    hide();
    event(piwikEvents && piwikEvents.overlay ? piwikEvents.overlay : ['click', modalName, "close_overlay"]);
  };

  return isShowing ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ModalOverlay, null), /*#__PURE__*/React.createElement(ModalWrapper, {
    "aria-modal": true,
    "aria-hidden": true,
    tabIndex: -1,
    role: "dialog",
    onClick: backdropClosesModal ? handleBackDropClick : null
  }, /*#__PURE__*/React.createElement(ModalContainer, rest, /*#__PURE__*/React.createElement(ModalHeader, null, /*#__PURE__*/React.createElement(ModalCloseButton, {
    type: "button",
    "data-dismiss": "modal",
    "aria-label": "Close",
    onClick: function onClick() {
      hide();
      event(piwikEvents && piwikEvents.closeButton ? piwikEvents.closeButton : ['click', modalName, "close_x_button"]);
    }
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, "\xD7"))), children))), document.body) : null;
};
ModalComponent.propTypes = {
  /** The contents of the modal */
  children: PropTypes.any.isRequired,

  /** Is the modal visible? Should be passed in via the useModal hook */
  isShowing: PropTypes.bool.isRequired,

  /** Function to close the modal. Should be passed in via the toggleModal attribute from the useModal hook */
  hide: PropTypes.func.isRequired,

  /** Close modal by clicking outside */
  backdropClosesModal: PropTypes.bool,

  /** Modal name, like `signup-modal` for example. Used for piwik event tracking. */
  modalName: PropTypes.string.isRequired,

  /** An optional object consisting of 2 custom events: overlay & closeButton. Object keys must be `overlay` and `closeButton` with values as arrays in the form of [category, action, name, value], though name and value are optional. */
  piwikEvents: PropTypes.object
};
export var Modal = createThemedComponent('Modal', ModalComponent);
export default Modal;